import React, { useState, useEffect } from 'react';
import {
  Textarea,
  Switch,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from '@heroui/react';
import {
  AlertBlock,
  CardWrapper,
  CustomHeroButton,
  CustomHeroInput,
  ResponsiveModal,
} from '../../components';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslations } from '../../contexts/translation.context';
import partnerAuthService from '../../api/partner/partnerAuthService';
import { showError } from '../../utils/toast';
import { Typography } from '../../components';
import { GreenTgIc, InfoSuccessGreen } from '../../components/icons';
import { CustomCheckbox } from '../../components';
import { useFormik } from 'formik';
import { createModerationSchema, ModerationFormData } from './validation';
import CustomTextarea from '../../components/CustomTextarea';

const EXPERIENCE_MAX_LENGTH = 500;

const ModerationDashboard: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslations();
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const formik = useFormik<ModerationFormData>({
    initialValues: {
      whatsApp: '',
      volume: '',
      tgTag: '',
      language: '',
      sourceInfoAboutUs: '',
      experienceDescription: '',
      sources: [],
      hasOnlyFansExperience: false,
    },
    validationSchema: createModerationSchema(t),
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        const payload = {
          whats_app: values.whatsApp,
          sources: values.sources,
          traffic_volume: values.volume,
          has_only_fans_experience: values.hasOnlyFansExperience,
          tg_tag: values.tgTag,
          language: values.language,
          source_info_about_us: values.sourceInfoAboutUs,
          only_fans_experience_description: values.experienceDescription,
        };

        await partnerAuthService.moderationAccount(payload);

        await refreshUser();

        setShowSuccessModal(true);
      } catch (error: any) {
        const detail = error?.response?.data?.detail;
        if (error?.response?.status === 422 && Array.isArray(detail)) {
          const fieldNameMap: Record<string, keyof ModerationFormData> = {
            only_fans_experience_description: 'experienceDescription',
            tg_tag: 'tgTag',
            traffic_volume: 'volume',
            sources: 'sources',
            source_info_about_us: 'sourceInfoAboutUs',
            language: 'language',
            whats_app: 'whatsApp',
            has_only_fans_experience: 'hasOnlyFansExperience',
          };

          detail.forEach((item: any) => {
            const backendField = item?.loc?.[item?.loc?.length - 1];
            const formField = fieldNameMap[backendField];
            if (!formField) return;
            formik.setFieldTouched(formField, true, false);
            formik.setFieldError(formField, item?.msg || t('moderation.submitError'));
          });

          const firstMessage = detail?.[0]?.msg;
          showError(firstMessage || t('moderation.submitError'));
        } else {
          const errorMessage =
            (typeof detail === 'string' ? detail : null) || error?.message || t('moderation.submitError');
          showError(errorMessage);
        }
      } finally {
        setIsLoading(false);
      }
    },
  });

  const trafficSources = [
    { label: 'OnlyFans', value: 'of' },
    { label: 'Reddit', value: 'reddit' },
    { label: 'Instagram', value: 'inst' },
    { label: 'TikTok', value: 'tt' },
    { label: 'Google', value: 'google' },
    { label: 'Telegram', value: 'tg' },
    { label: 'Twitter', value: 'twitter' },
    { label: 'Youtube', value: 'youtube' },
    { label: 'Другие', value: 'another' },
  ];

  const handleTrafficSourceChange = (sourceValue: string, checked: boolean) => {
    const currentSources = formik.values.sources || [];
    if (checked) {
      formik.setFieldValue('sources', [...currentSources, sourceValue]);
    } else {
      formik.setFieldValue(
        'sources',
        currentSources.filter((s) => s !== sourceValue)
      );
    }
  };

  const { setValues } = formik;

  useEffect(() => {
    if (user) {
      setValues({
        whatsApp: user.whats_app || '',
        volume: user.traffic_volume || '',
        tgTag: user.tg_tag || '',
        language: user.language || '',
        sourceInfoAboutUs: user.source_info_about_us || '',
        experienceDescription: user.only_fans_experience_description || '',
        sources: user.sources && Array.isArray(user.sources) ? user.sources : [],
        hasOnlyFansExperience: user.has_only_fans_experience || false,
      });
    }
  }, [user, setValues]);

  const isModerationSubmitted = user?.status === 'wait_moderation';

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">{t('partner.moderation')}</h1>
      </div>

      <div className="mb-6">
        <AlertBlock
          data={[
            {
              severity: 'info',
              title: t('dashboard.welcome'),
              description: t('dashboard.partnerWelcomeDescription'),
            },
          ]}
        />
      </div>

      <CardWrapper>
        <div className="mb-6">
          <h3 className="text-sm font-normal text-gray-900 mb-2">
            {t('partner.trafficSources')}
            <span className="text-partner">*</span>
          </h3>
          <div className="flex flex-wrap gap-4 gap-y-2">
            {trafficSources.map((source) => (
              <div key={source.value}>
                <CustomCheckbox
                  size="md"
                  isSelected={(formik.values.sources || []).includes(source.value)}
                  onValueChange={(checked) => handleTrafficSourceChange(source.value, checked)}
                >
                  <span className="text-base text-black">{source.label}</span>
                </CustomCheckbox>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <CustomHeroInput
            label={t('partner.trafficVolume')}
            placeholder={t('partner.emptyPlaceholder')}
            required
            value={formik.values.volume}
            onChange={(e) => formik.setFieldValue('volume', e.target.value)}
            onBlur={formik.handleBlur}
            variant="bordered"
            labelPlacement="outside"
            className="w-full"
            errorMessage={formik.touched.volume && formik.errors.volume}
            isInvalid={formik.touched.volume && !!formik.errors.volume}
          />
        </div>

        <div className="mb-2">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-normal text-gray-900">
              {t('partner.onlyFansExperience')}
            </label>
            <div className="flex items-center gap-2">
              <Switch
                isSelected={formik.values.hasOnlyFansExperience}
                onValueChange={(value) => formik.setFieldValue('hasOnlyFansExperience', value)}
                size="sm"
                style={
                  {
                    '--nextui-primary': '#006FEE',
                  } as React.CSSProperties
                }
                classNames={{
                  wrapper: formik.values.hasOnlyFansExperience ? '!bg-partner' : '',
                  thumb: '!bg-white',
                }}
              />
              <span className="text-sm text-gray-900">{t('partner.experienceDescription')}</span>
            </div>
          </div>
        </div>

        {formik.values.hasOnlyFansExperience && (
          <div className="">
            <h3 className="text-sm font-normal text-gray-900 mb-2">
              {t('partner.experienceLabel')}
              <span className="text-partner">*</span>
            </h3>
            <Textarea
              placeholder={t('partner.experiencePlaceholder') || 'Опишите ваш опыт работы...'}
              value={formik.values.experienceDescription}
              onChange={(e) => {
                const nextValue = e.target.value.slice(0, EXPERIENCE_MAX_LENGTH);
                formik.setFieldValue('experienceDescription', nextValue);
              }}
              onBlur={formik.handleBlur}
              variant="bordered"
              minRows={3}
              maxLength={EXPERIENCE_MAX_LENGTH}
              className="w-full"
              labelPlacement="outside"
              errorMessage={
                formik.touched.experienceDescription && formik.errors.experienceDescription
              }
              isInvalid={
                formik.touched.experienceDescription && !!formik.errors.experienceDescription
              }
            />
            <div className="mt-1 text-right text-xs text-gray-500">
              {formik.values.experienceDescription.length}/{EXPERIENCE_MAX_LENGTH}
            </div>
          </div>
        )}

        <div className="mb-6 mt-4">
          <CustomHeroInput
            label={t('partner.telegram')}
            required
            placeholder={t('partner.telegramPlaceholder')}
            value={formik.values.tgTag}
            onChange={(e) => formik.setFieldValue('tgTag', e.target.value)}
            onBlur={formik.handleBlur}
            variant="bordered"
            className="w-full"
            labelPlacement="outside"
            errorMessage={formik.touched.tgTag && formik.errors.tgTag}
            isInvalid={formik.touched.tgTag && !!formik.errors.tgTag}
          />
        </div>

        <div className="mb-6">
          <CustomTextarea
            label={t('labels.otherContacts')}
            placeholder={t('partner.telegramWhatsapp')}
            value={formik.values.whatsApp}
            onChange={(e) => formik.setFieldValue('whatsApp', e.target.value)}
            onBlur={formik.handleBlur}
            variant="bordered"
            minRows={3}
            labelPlacement="outside"
            errorMessage={formik.touched.whatsApp && formik.errors.whatsApp}
            isInvalid={formik.touched.whatsApp && !!formik.errors.whatsApp}
          />
        </div>

        <div className="mb-6">
          <CustomTextarea
            label={t('partner.languages')}
            placeholder={t('partner.languagesPlaceholder')}
            value={formik.values.language}
            onChange={(e) => formik.setFieldValue('language', e.target.value)}
            onBlur={formik.handleBlur}
            variant="bordered"
            minRows={3}
            labelPlacement="outside"
            errorMessage={formik.touched.language && formik.errors.language}
            isInvalid={formik.touched.language && !!formik.errors.language}
          />
        </div>

        <div className="mb-6">
          <CustomTextarea
            label={t('partner.howHeard')}
            placeholder={t('partner.howHeardPlaceholder')}
            value={formik.values.sourceInfoAboutUs}
            onChange={(e) => formik.setFieldValue('sourceInfoAboutUs', e.target.value)}
            onBlur={formik.handleBlur}
            variant="bordered"
            minRows={3}
            labelPlacement="outside"
            errorMessage={formik.touched.sourceInfoAboutUs && formik.errors.sourceInfoAboutUs}
            isInvalid={formik.touched.sourceInfoAboutUs && !!formik.errors.sourceInfoAboutUs}
          />
        </div>
      </CardWrapper>

      {/* Отображение общих ошибок валидации */}
      {Object.keys(formik.errors).length > 0 && formik.submitCount > 0 && (
        <div className="mt-6">
          <AlertBlock
            data={Object.entries(formik.errors).map(([field, error]) => ({
              severity: 'error' as const,
              description: error as string,
            }))}
            type="rounded"
          />
        </div>
      )}

      <div className="flex justify-start mt-6">
        <CustomHeroButton
          variant="primary"
          onPress={formik.handleSubmit}
          className="px-4 py-2 bg-partner text-white rounded-xl"
          title={isLoading ? t('common.loading') || 'Loading...' : t('partner.sendModeration')}
          disabled={isLoading}
        />
      </div>

      {isModerationSubmitted && (
        <div className="mt-6">
          <AlertBlock
            data={[
              {
                severity: 'success',
                title: `${t('moderation.requestSent')} ${new Date().toLocaleTimeString('ru-RU', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}, ${new Date().toLocaleDateString('ru-RU', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })}`,
                // description: t('moderation.managerWillContact'),
                icon: <InfoSuccessGreen />,
              },
              {
                severity: 'info',
                title: t('moderation.noResponseTitle'),
                description: t('moderation.noResponseDescription'),
                icon: (
                  <div className="ml-[-.25rem] mr-[-.5rem]">
                    <GreenTgIc />
                  </div>
                ),
              },
            ]}
            type="default"
            className="border-l-successDark bg-[#f8faf9]"
            titleColor="#0e793c"
          />
        </div>
      )}

      <ResponsiveModal
        isOpen={showSuccessModal}
        onOpenChange={setShowSuccessModal}
        placement="center"
        classNames={{
          base: 'bg-white w-[25rem]',
          backdrop: 'bg-black/50',
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col p-6 gap-1">
                <Typography variant="h6">{t('moderation.requestSent')}</Typography>
              </ModalHeader>
              <ModalBody>
                <Typography variant="body2">{t('moderation.requestSentSuccess')}</Typography>
              </ModalBody>
              <ModalFooter>
                <Button color="primary" onPress={onClose} className="bg-partner text-white">
                  {t('successModal.close')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </ResponsiveModal>
    </div>
  );
};

export default ModerationDashboard;
